import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { uploadFilesSequentially } from "@/lib/uploadFiles"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  CheckCircle2,
  ChevronDown,
  Clock,
  Eye,
  FilePlus2,
  FileSpreadsheet,
  FolderOpen,
  Loader2,
  RefreshCw,
  ScanSearch,
  ShieldAlert,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import {
  DataCard,
  DataCardActions,
  DataCardField,
  DataCardGrid,
  DataCardHeader,
  DataCardStatus,
  DataCardTitle,
} from "@/components/ui/data-card"
import { MobileFilterSheet } from "@/components/ui/mobile-filter-sheet"
import { MobileFilterSortBar } from "@/components/ui/mobile-filter-sort-bar"
import { MobileSortSheet, type SortOption } from "@/components/ui/mobile-sort-sheet"
import { useMobileFilterSort } from "@/hooks/use-mobile-filter-sort"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { consumer } from "@/lib/actioncable"

const SORT_OPTIONS: SortOption[] = [
  { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
  { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
  { sort: "source_folder", direction: "asc", label: "Sumber A–Z" },
  { sort: "source_folder", direction: "desc", label: "Sumber Z–A" },
  { sort: "file_count", direction: "desc", label: "File terbanyak" },
  { sort: "file_count", direction: "asc", label: "File paling sedikit" },
  { sort: "total_bytes", direction: "desc", label: "Ukuran terbesar" },
  { sort: "total_bytes", direction: "asc", label: "Ukuran terkecil" },
  { sort: "status", direction: "asc", label: "Status A–Z" },
  { sort: "status", direction: "desc", label: "Status Z–A" },
]

const ALLOWED_EXTENSIONS = [".xlsx", ".xls", ".xlsm", ".csv", ".txt"]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RunStatus = "pending" | "processing" | "completed" | "failed" | "cancelled"

type RunRow = {
  id: number
  source_folder: string
  subpath: string | null
  status: RunStatus
  file_count: number
  total_bytes: number
  source_mapped: boolean | null
  meta_files_written: number | null
  new_template_count: number
  integrity_issue_count: number
  ingest_ok_count: number | null
  error_message: string | null
  generated_at: string | null
  created_at: string
  uploaded_by: string | null
}

type SelectedFile = {
  file: File
  relativePath: string
}

type PreviewFileOk = {
  relative_path: string
  byte_size: number
  destination: string
  would_overwrite: boolean
}

type PreviewFileError = { relative_path: string; byte_size: number; error: string }

type PreviewFile = PreviewFileOk | PreviewFileError

function isPreviewError(f: PreviewFile): f is PreviewFileError {
  return "error" in f
}

type PreviewResponse = {
  source_folder: string
  source_exists: boolean
  total_bytes: number
  files: PreviewFile[]
}

type Filters = {
  source: string | null
  status: string | null
  search: string | null
}

// DataXrayRunChannel messages — the subset of run fields the job broadcasts on
// each transition, merged into the matching live row without a full reload.
type StatusUpdate = {
  type: "status_update"
  run_id: number
  status: RunStatus
  source_mapped: boolean | null
  meta_files_written: number | null
  new_template_count: number
  integrity_issue_count: number
  ingest_ok_count: number | null
  error_message: string | null
  generated_at: string | null
}

type ProgressUpdate = {
  type: "progress_update"
  run_id: number
  message: string
}

const TERMINAL_STATUSES: RunStatus[] = ["completed", "failed", "cancelled"]

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AdminDataXrayRuns({
  runs,
  in_flight_runs,
  total,
  page,
  per_page,
  sort,
  direction,
  filters,
  filter_options,
  source_folders,
}: {
  runs: RunRow[]
  in_flight_runs: RunRow[]
  total: number
  page: number
  per_page: number
  sort: string
  direction: "asc" | "desc"
  filters: Filters
  filter_options: { sources: string[]; statuses: string[] }
  source_folders: string[]
}) {
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const folderInputRef = React.useRef<HTMLInputElement>(null)
  const activeXhrRef = React.useRef<XMLHttpRequest | null>(null)
  const uploadAbortedRef = React.useRef(false)
  const previewAbortRef = React.useRef<AbortController | null>(null)
  const runIdRef = React.useRef<number | null>(null)

  const [sourceFolder, setSourceFolder] = React.useState("")
  const [selectedFiles, setSelectedFiles] = React.useState<SelectedFile[]>([])
  const [preview, setPreview] = React.useState<PreviewResponse | null>(null)
  const [previewError, setPreviewError] = React.useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = React.useState(false)
  const [checkedPaths, setCheckedPaths] = React.useState<Set<string>>(new Set())
  const [uploading, setUploading] = React.useState(false)
  const [uploadProgress, setUploadProgress] = React.useState<number | null>(null)
  const [dragOver, setDragOver] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState(filters.search ?? "")
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())
  const [purgeFiles, setPurgeFiles] = React.useState(false)
  const [liveRuns, setLiveRuns] = React.useState<RunRow[]>(runs)
  const [liveInFlight, setLiveInFlight] = React.useState<RunRow[]>(in_flight_runs)
  const [progressMap, setProgressMap] = React.useState<Record<number, string>>({})

  // Sync search input when URL changes (browser back/forward)
  React.useEffect(() => {
    setSearchValue(filters.search ?? "")
  }, [filters.search])

  React.useEffect(() => { setLiveRuns(runs) }, [runs])
  React.useEffect(() => { setLiveInFlight(in_flight_runs) }, [in_flight_runs])

  // Subscribe to the FULL active set (across all pages), sourced from the
  // in-flight panel — not just the in-flight rows on the current history page.
  // This is what makes a multi-run generation batch update live. SSR-safe:
  // ActionCable is only touched inside this effect.
  const liveInFlightKey = liveInFlight
    .map((r) => r.id)
    .sort((a, b) => a - b)
    .join(",")

  React.useEffect(() => {
    if (!liveInFlightKey) return
    const ids = liveInFlightKey.split(",").map((s) => parseInt(s, 10))

    function mergeRow(prev: RunRow[], data: StatusUpdate): RunRow[] {
      return prev.map((r) =>
        r.id === data.run_id
          ? {
              ...r,
              status: data.status,
              source_mapped: data.source_mapped,
              meta_files_written: data.meta_files_written,
              new_template_count: data.new_template_count,
              integrity_issue_count: data.integrity_issue_count,
              ingest_ok_count: data.ingest_ok_count,
              error_message: data.error_message,
              generated_at: data.generated_at,
            }
          : r,
      )
    }

    const subs = ids.map((id) =>
      consumer.subscriptions.create(
        { channel: "DataXrayRunChannel", run_id: id },
        {
          received(data: StatusUpdate | ProgressUpdate) {
            if (data.type === "progress_update") {
              setProgressMap((prev) => ({ ...prev, [data.run_id]: data.message }))
              return
            }

            setLiveRuns((prev) => mergeRow(prev, data))

            if (TERMINAL_STATUSES.includes(data.status)) {
              setProgressMap((prev) => {
                const next = { ...prev }
                delete next[data.run_id]
                return next
              })
              setLiveInFlight((prev) => prev.filter((r) => r.id !== data.run_id))
              router.reload({ only: ["runs", "in_flight_runs", "source_folders", "filter_options"] })
            } else {
              setLiveInFlight((prev) => mergeRow(prev, data))
            }
          },
        },
      ),
    )
    return () => subs.forEach((s) => s.unsubscribe())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveInFlightKey])

  // -------------------------------------------------------------------------
  // Navigation helper (URL-based filter/sort/page state)
  // -------------------------------------------------------------------------

  function navigate(overrides: Record<string, string | number | null>) {
    const params: Record<string, string | number> = {}
    if (filters.source)        params.source    = filters.source
    if (filters.status)        params.status    = filters.status
    if (filters.search)        params.search    = filters.search
    if (sort !== "created_at") params.sort      = sort
    if (direction !== "desc")  params.direction = direction
    if (page > 1)              params.page      = page
    Object.entries(overrides).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") params[k] = v as string | number
      else delete params[k]
    })
    setSelectedIds(new Set())
    router.get("/admin/data-xray", params as Record<string, string>, {
      preserveScroll: false,
    })
  }

  function handleSortColumn(col: string) {
    if (sort === col) {
      navigate({ direction: direction === "asc" ? "desc" : "asc", page: null })
    } else {
      navigate({ sort: col, direction: "asc", page: null })
    }
  }

  function historyParams(): Record<string, string> {
    const params: Record<string, string> = {}
    if (filters.source)        params.source    = filters.source
    if (filters.status)        params.status    = filters.status
    if (filters.search)        params.search    = filters.search
    if (sort !== "created_at") params.sort      = sort
    if (direction !== "desc")  params.direction = direction
    return params
  }

  // -------------------------------------------------------------------------
  // File selection (file picker / folder picker / drag-drop)
  // -------------------------------------------------------------------------

  function hasAllowedExtension(name: string) {
    const lower = name.toLowerCase()
    return ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext))
  }

  function addFiles(files: FileList | File[] | null) {
    if (!files) return
    const incoming: SelectedFile[] = Array.from(files)
      .filter((f) => hasAllowedExtension(f.name) && !f.name.startsWith("_") && !f.name.startsWith("."))
      .map((f) => ({
        // webkitRelativePath preserves the picked folder's subtree — exactly
        // the year/distributor/segment axes the x-ray's parse_axes reads.
        relativePath: (f.webkitRelativePath || f.name).replace(/\\/g, "/"),
        file: f,
      }))
    if (incoming.length === 0) return
    setSelectedFiles((prev) => {
      const byPath = new Map(prev.map((s) => [s.relativePath, s]))
      incoming.forEach((s) => byPath.set(s.relativePath, s))
      return Array.from(byPath.values())
    })
    if (fileInputRef.current) fileInputRef.current.value = ""
    if (folderInputRef.current) folderInputRef.current.value = ""
  }

  function removeFile(relativePath: string) {
    setSelectedFiles((prev) => prev.filter((s) => s.relativePath !== relativePath))
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    addFiles(e.dataTransfer.files)
  }

  function resetUploadState() {
    setSelectedFiles([])
    setPreview(null)
    setPreviewError(null)
    setCheckedPaths(new Set())
    setUploadProgress(null)
    runIdRef.current = null
    if (fileInputRef.current) fileInputRef.current.value = ""
    if (folderInputRef.current) folderInputRef.current.value = ""
  }

  // -------------------------------------------------------------------------
  // Destination / overwrite preview (raw fetch, not the Inertia router)
  // -------------------------------------------------------------------------

  const sourceFolderValid = /^[a-z0-9][a-z0-9-]*$/.test(sourceFolder)

  React.useEffect(() => {
    if (!sourceFolderValid || selectedFiles.length === 0) {
      setPreview(null)
      setPreviewError(null)
      return
    }

    const controller = new AbortController()
    previewAbortRef.current?.abort()
    previewAbortRef.current = controller
    setPreviewLoading(true)
    setPreviewError(null)

    const timer = window.setTimeout(async () => {
      try {
        const resp = await fetch("/admin/data-xray/preview", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          credentials: "same-origin",
          body: JSON.stringify({
            source_folder: sourceFolder,
            files: selectedFiles.map((s) => ({
              relative_path: s.relativePath,
              byte_size: s.file.size,
            })),
          }),
          signal: controller.signal,
        })
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}))
          throw new Error((err as { error?: string }).error ?? `HTTP ${resp.status}`)
        }
        const data: PreviewResponse = await resp.json()
        setPreview(data)
        // Every valid file starts checked — overwriting is a legit refresh
        // flow here (re-exported files); the badge makes it visible.
        setCheckedPaths(new Set(data.files.filter((f) => !isPreviewError(f)).map((f) => f.relative_path)))
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return
        setPreview(null)
        setPreviewError(err instanceof Error ? err.message : "Gagal memuat preview tujuan.")
      } finally {
        if (previewAbortRef.current === controller) {
          setPreviewLoading(false)
          previewAbortRef.current = null
        }
      }
    }, 250)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [sourceFolder, sourceFolderValid, selectedFiles])

  function toggleChecked(relativePath: string) {
    setCheckedPaths((prev) => {
      const next = new Set(prev)
      if (next.has(relativePath)) next.delete(relativePath)
      else next.add(relativePath)
      return next
    })
  }

  // -------------------------------------------------------------------------
  // Upload (sequential, one file per request; first response returns run_id)
  // -------------------------------------------------------------------------

  async function handleConfirmUpload() {
    const toUpload = selectedFiles.filter((s) => checkedPaths.has(s.relativePath))
    if (toUpload.length === 0 || !sourceFolderValid) return

    setUploading(true)
    setUploadProgress(0)
    uploadAbortedRef.current = false
    runIdRef.current = null

    const pathByFile = new Map(toUpload.map((s) => [s.file, s.relativePath]))

    try {
      const { errors, aborted } = await uploadFilesSequentially<{ run_id: number }>({
        url: "/admin/data-xray",
        files: toUpload.map((s) => s.file),
        abortRef: uploadAbortedRef,
        xhrRef: activeXhrRef,
        onProgress: setUploadProgress,
        fields: (file) => {
          const fields: Record<string, string> = {
            source_folder: sourceFolder,
            relative_path: pathByFile.get(file) ?? file.name,
          }
          if (runIdRef.current) fields.run_id = String(runIdRef.current)
          return fields
        },
        onFileDone: ({ data }) => {
          if (!runIdRef.current && data.run_id) runIdRef.current = data.run_id
        },
      })
      if (aborted) {
        // Leave the partially-uploaded run pending; the in-flight panel offers
        // cancel, after which it can be deleted.
        router.reload({ only: ["runs", "in_flight_runs", "source_folders", "filter_options"] })
        return
      }

      if (errors.length) alert(`Sebagian file gagal diunggah:\n${errors.join("\n")}`)

      const runId = runIdRef.current
      resetUploadState()
      if (runId) {
        // Finalize the batch. In Milestone 1 this marks the run completed with
        // an honest "generation lands in M2" note; M2 swaps in the real job.
        router.post(`/admin/data-xray/${runId}/generate`, historyParams())
      } else {
        router.reload({ only: ["runs", "in_flight_runs", "source_folders", "filter_options"] })
      }
    } finally {
      setUploading(false)
      setUploadProgress(null)
    }
  }

  function handleAbortUpload() {
    uploadAbortedRef.current = true
    activeXhrRef.current?.abort()
    activeXhrRef.current = null
  }

  // -------------------------------------------------------------------------
  // Run actions (Inertia router — controller redirects)
  // -------------------------------------------------------------------------

  function handleCancelRun(id: number) {
    router.patch(`/admin/data-xray/${id}/cancel`, historyParams())
  }

  // Re-run the generator for an existing run (auto after upload uses the same
  // endpoint). A terminal run is reset to pending server-side, so it reappears
  // in the in-flight panel and streams live again.
  function handleRegenerate(id: number) {
    router.post(`/admin/data-xray/${id}/generate`, historyParams(), {
      onSuccess: () => router.reload({ only: ["runs", "in_flight_runs"] }),
    })
  }

  function deleteParams(): Record<string, string> {
    const params = historyParams()
    if (purgeFiles) params.purge_files = "1"
    return params
  }

  function handleDeleteRun(run: RunRow) {
    const suffix = purgeFiles
      ? "\n\nFile di disk JUGA akan dihapus dari folder data x-ray."
      : "\n\nFile di disk dipertahankan (hanya catatan riwayat yang dihapus)."
    if (!window.confirm(`Hapus run "${run.source_folder}" (${run.file_count} file)?${suffix}`)) return
    router.delete(`/admin/data-xray/${run.id}`, { data: deleteParams() })
  }

  function handleBulkDelete() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    const suffix = purgeFiles
      ? "\n\nFile di disk JUGA akan dihapus dari folder data x-ray."
      : "\n\nFile di disk dipertahankan (hanya catatan riwayat yang dihapus)."
    if (!window.confirm(`Hapus ${ids.length} run terpilih?${suffix}`)) return
    router.delete("/admin/data-xray/bulk_destroy", {
      data: { ids, ...deleteParams() },
      onFinish: () => setSelectedIds(new Set()),
    })
  }

  // -------------------------------------------------------------------------
  // Bulk selection
  // -------------------------------------------------------------------------

  const deletableRuns = React.useMemo(
    () => liveRuns.filter((r) => r.status !== "pending" && r.status !== "processing"),
    [liveRuns]
  )

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    if (selectedIds.size === deletableRuns.length && deletableRuns.length > 0) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(deletableRuns.map((r) => r.id)))
    }
  }

  // -------------------------------------------------------------------------
  // Computed
  // -------------------------------------------------------------------------

  const validPreviewFiles = preview?.files.filter((f): f is PreviewFileOk => !isPreviewError(f)) ?? []
  const errorPreviewFiles = preview?.files.filter(isPreviewError) ?? []
  const overwriteCount = validPreviewFiles.filter((f) => f.would_overwrite).length
  const noneChecked = checkedPaths.size === 0
  const hasActiveFilter = !!filters.source || !!filters.status || !!filters.search

  const { filterOpen, setFilterOpen, sortOpen, setSortOpen, activeFilterCount, applyFilters, resetFilters } =
    useMobileFilterSort(filters, navigate, ["source", "status", "search"] as const)
  const sortLabel =
    SORT_OPTIONS.find((o) => o.sort === sort && o.direction === direction)?.label ?? "Urutkan"
  const totalPages = Math.ceil(total / per_page)

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <Head title="Data X-Ray">
        <meta
          name="description"
          content="Upload data mentah ke pohon x-ray dan kelola riwayat regenerasi meta-index Data-Project."
        />
        <meta property="og:title" content="Data X-Ray" />
        <meta
          property="og:description"
          content="Upload data mentah ke pohon x-ray dan kelola riwayat regenerasi meta-index Data-Project."
        />
      </Head>
      <AppShell width="wide">
        {/* Header */}
        <div className="border-b border-hairline pb-6">
          <div className="flex items-start gap-3">
            <ScanSearch className="mt-1 h-6 w-6 shrink-0 text-ink-muted" />
            <div>
              <h1>Data X-Ray</h1>
              <p className="mt-1">
                Upload file/folder data mentah ke <code>data/Data-Project/&lt;sumber&gt;/…</code>.
                Subfolder dipertahankan sebagai axis tahun/distributor/segmen milik x-ray.
              </p>
            </div>
          </div>
        </div>

        {/* Upload panel */}
        <div className="mt-6 rounded-md border border-hairline bg-surface p-4 sm:p-6">
          <h2>Upload Data</h2>

          <div className="mt-4 max-w-md">
            <label htmlFor="source-folder">Folder sumber</label>
            <SourceFolderCombobox
              id="source-folder"
              value={sourceFolder}
              onChange={setSourceFolder}
              options={source_folders}
              disabled={uploading}
            />
            {sourceFolder !== "" && !sourceFolderValid && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                Nama folder harus kebab-case huruf kecil (contoh: <code>b2b-sell-in</code>).
              </p>
            )}
            {sourceFolderValid && !source_folders.includes(sourceFolder) && (
              <p className="mt-1 text-xs text-ink-muted">
                <FilePlus2 className="mr-1 inline h-3 w-3" />
                Folder sumber <strong>baru</strong> — x-ray struktural digenerate, konteks bisnis
                membaca <em>(belum dipetakan)</em> sampai sumber di-onboard ke <code>CTX</code>.
              </p>
            )}
          </div>

          {/* Pickers + drop zone */}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="mr-2 h-4 w-4" />
              Pilih File
            </Button>
            <Button
              variant="secondary"
              onClick={() => folderInputRef.current?.click()}
              disabled={uploading}
            >
              <FolderOpen className="mr-2 h-4 w-4" />
              Pilih Folder
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_EXTENSIONS.join(",")}
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
            {...({ webkitdirectory: "", mozdirectory: "" } as React.InputHTMLAttributes<HTMLInputElement>)}
          />

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={[
              "mt-4 flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors",
              dragOver ? "border-accent bg-accent-faded" : "border-hairline",
            ].join(" ")}
          >
            <FileSpreadsheet className="mb-2 h-8 w-8 text-ink-muted" />
            <p className="text-sm font-medium text-ink-display">
              Drag &amp; drop file ke sini, atau pilih file/folder
            </p>
            <p className="mt-1 text-xs text-ink-muted">
              Hanya {ALLOWED_EXTENSIONS.join(" / ")} — nama berawalan <code>_</code> atau{" "}
              <code>.</code> ditolak (bentrok dengan file meta x-ray).
            </p>
          </div>

          {/* Selected files + destination preview */}
          {selectedFiles.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between">
                <h3>
                  {selectedFiles.length} file dipilih
                  {preview ? ` — total ${formatBytes(preview.total_bytes)}` : ""}
                </h3>
                <Button variant="ghost" size="sm" onClick={resetUploadState} disabled={uploading}>
                  Bersihkan
                </Button>
              </div>

              {!sourceFolderValid && (
                <p className="mt-2 text-sm text-ink-muted">
                  Isi nama folder sumber dulu untuk melihat tujuan penulisan.
                </p>
              )}
              {previewLoading && (
                <div className="mt-3 flex items-center gap-2 text-ink-muted">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Menghitung tujuan…</span>
                </div>
              )}
              {previewError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{previewError}</p>
              )}

              {preview && !previewLoading && (
                <>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                    <Badge tone={preview.source_exists ? "success" : "accent"}>
                      {preview.source_exists ? "Sumber existing" : "Sumber baru"}
                    </Badge>
                    {overwriteCount > 0 && (
                      <Badge tone="signal">{overwriteCount} file akan menimpa</Badge>
                    )}
                    {errorPreviewFiles.length > 0 && (
                      <Badge tone="danger">{errorPreviewFiles.length} file ditolak</Badge>
                    )}
                  </div>

                  <div className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1">
                    {preview.files.map((f) =>
                      isPreviewError(f) ? (
                        <div
                          key={f.relative_path}
                          className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs dark:border-red-700 dark:bg-red-950"
                        >
                          <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                          <div className="min-w-0">
                            <p className="break-all font-medium text-ink-display">{f.relative_path}</p>
                            <p className="mt-0.5 text-red-700 dark:text-red-300">{f.error}</p>
                          </div>
                          <button
                            onClick={() => removeFile(f.relative_path)}
                            className="ml-auto shrink-0 rounded p-0.5 text-ink-muted hover:text-red-600"
                            title="Hapus dari daftar"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div
                          key={f.relative_path}
                          className={[
                            "flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
                            f.would_overwrite ? "border-signal bg-signal-faded" : "border-hairline bg-page",
                            !checkedPaths.has(f.relative_path) ? "opacity-60" : "",
                          ].join(" ")}
                        >
                          <Checkbox
                            checked={checkedPaths.has(f.relative_path)}
                            onChange={() => toggleChecked(f.relative_path)}
                            className="mt-0.5 shrink-0"
                            aria-label={`Sertakan ${f.relative_path}`}
                            disabled={uploading}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="break-all font-medium text-ink-display">{f.relative_path}</p>
                            <p className="mt-0.5 break-all text-ink-muted">
                              → <code>{f.destination}</code> · {formatBytes(f.byte_size)}
                            </p>
                          </div>
                          {f.would_overwrite && (
                            <Badge tone="signal" className="shrink-0">Menimpa</Badge>
                          )}
                        </div>
                      )
                    )}
                  </div>

                  {uploading && uploadProgress !== null && (
                    <div className="mt-4 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-ink-muted">
                          {uploadProgress < 100
                            ? `Mengirim file… ${uploadProgress}%`
                            : "File terkirim, menyelesaikan…"}
                        </p>
                        {uploadProgress < 100 && (
                          <Button variant="ghost" size="sm" onClick={handleAbortUpload}>
                            <Ban className="mr-1 h-3 w-3" />
                            Batal
                          </Button>
                        )}
                      </div>
                      <ProgressBar value={uploadProgress} />
                    </div>
                  )}

                  <div className="mt-4 flex gap-3">
                    <Button
                      variant="primary"
                      onClick={handleConfirmUpload}
                      disabled={uploading || noneChecked || !sourceFolderValid}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Mengunggah…
                        </>
                      ) : (
                        `Upload ${checkedPaths.size} file`
                      )}
                    </Button>
                    <Button variant="secondary" onClick={resetUploadState} disabled={uploading}>
                      Batal
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* In-flight panel — always above history, sourced off the FULL active
            set (all admins' pending/processing runs) and updated live. */}
        {liveInFlight.length > 0 && (
          <div className="mt-8">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="mb-0">Sedang Diproses</h2>
              <Badge tone="accent">{liveInFlight.length} aktif</Badge>
            </div>
            <div className="space-y-2">
              {liveInFlight.map((r) => (
                <div
                  key={r.id}
                  className="flex flex-wrap items-center gap-3 rounded-md border border-hairline bg-surface px-4 py-3"
                >
                  <StatusBadge status={r.status} />
                  <span className="font-medium text-ink-display">{r.source_folder}</span>
                  {r.subpath && <code className="text-xs text-ink-muted">{r.subpath}</code>}
                  <span className="text-xs text-ink-muted">
                    {r.file_count} file · {formatBytes(r.total_bytes)}
                  </span>
                  {r.status === "processing" && progressMap[r.id] && (
                    <span className="flex items-center gap-1 text-xs text-ink-muted">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {progressMap[r.id]}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto gap-1 text-red-600"
                    onClick={() => handleCancelRun(r.id)}
                  >
                    <Ban className="h-3.5 w-3.5" />
                    Batalkan
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        <div className="mt-10">
          <h2 className="mb-4">Riwayat Upload &amp; X-Ray</h2>

          {/* Filter + Sort bar (mobile) */}
          <div className="mb-4 md:hidden">
            <MobileFilterSortBar
              filterCount={activeFilterCount}
              sortLabel={sortLabel}
              onFilterClick={() => setFilterOpen(true)}
              onSortClick={() => setSortOpen(true)}
            />
          </div>

          {/* Filter bar (desktop) */}
          <div className="mb-4 hidden flex-wrap items-end gap-3 md:flex">
            <Select
              value={filters.source ?? ""}
              onChange={(e) => navigate({ source: e.target.value || null, page: null })}
              className="w-52"
            >
              <option value="">Semua Sumber</option>
              {filter_options.sources.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>

            <Select
              value={filters.status ?? ""}
              onChange={(e) => navigate({ status: e.target.value || null, page: null })}
              className="w-36"
            >
              <option value="">Semua Status</option>
              {filter_options.statuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                navigate({ search: searchValue || null, page: null })
              }}
            >
              <Input
                type="search"
                placeholder="Cari sumber / filename…"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onBlur={() => navigate({ search: searchValue || null, page: null })}
                className="w-52"
              />
            </form>

            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate({ source: null, status: null, search: null, page: null })}
              >
                Reset filter
              </Button>
            )}
          </div>

          {liveRuns.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {hasActiveFilter ? "Tidak ada run yang cocok dengan filter." : "Belum ada upload."}
            </p>
          ) : (
            <>
              {/* Bulk action bar */}
              {selectedIds.size > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-hairline bg-surface px-3 py-2">
                  <span className="text-xs text-ink-muted">{selectedIds.size} terpilih</span>
                  <Button variant="ghost" size="sm" type="button" onClick={handleBulkDelete}>
                    <Trash2 className="mr-1 h-3 w-3" />
                    Hapus terpilih
                  </Button>
                  <label className="flex items-center gap-1.5 font-normal text-ink-body">
                    <Checkbox
                      checked={purgeFiles}
                      onChange={() => setPurgeFiles((v) => !v)}
                    />
                    <span className="text-xs">Hapus juga file di disk</span>
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    className="ml-auto"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Batal pilih
                  </Button>
                </div>
              )}

              {/* Mobile card list */}
              <div className="space-y-3 md:hidden">
                {liveRuns.map((r) => (
                  <RunCard
                    key={r.id}
                    run={r}
                    selected={selectedIds.has(r.id)}
                    onToggleSelect={() => toggleSelect(r.id)}
                    onCancel={() => handleCancelRun(r.id)}
                    onDelete={() => handleDeleteRun(r)}
                    onRegenerate={() => handleRegenerate(r.id)}
                  />
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden overflow-hidden rounded-md border border-hairline md:block">
                <table className="w-full text-sm">
                  <thead className="bg-surface">
                    <tr>
                      <th className="w-10 px-3 py-2.5">
                        <Checkbox
                          checked={
                            deletableRuns.length > 0 &&
                            selectedIds.size === deletableRuns.length
                          }
                          onChange={toggleSelectAll}
                          aria-label="Pilih semua"
                          disabled={deletableRuns.length === 0}
                        />
                      </th>
                      <SortableHeader
                        col="source_folder" label="Sumber"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Subpath</th>
                      <SortableHeader
                        col="file_count" label="File" align="right"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="total_bytes" label="Ukuran" align="right"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="status" label="Status"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Oleh</th>
                      <SortableHeader
                        col="created_at" label="Waktu"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <th className="w-10 px-2 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hairline">
                    {liveRuns.map((r) => (
                      <RunTableRow
                        key={r.id}
                        run={r}
                        selected={selectedIds.has(r.id)}
                        onToggleSelect={() => toggleSelect(r.id)}
                        onCancel={() => handleCancelRun(r.id)}
                        onDelete={() => handleDeleteRun(r)}
                        onRegenerate={() => handleRegenerate(r.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Summary + pagination */}
          {total > 0 && (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-ink-muted">
                Menampilkan {(page - 1) * per_page + 1}–{Math.min(page * per_page, total)} dari{" "}
                {total} run
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => navigate({ page: page - 1 })}
                  >
                    Sebelumnya
                  </Button>
                  <span className="text-sm text-ink-muted">
                    Hal. {page} / {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => navigate({ page: page + 1 })}
                  >
                    Berikutnya
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile filter sheet */}
        <MobileFilterSheet
          open={filterOpen}
          onOpenChange={setFilterOpen}
          initial={{
            source: filters.source ?? "",
            status: filters.status ?? "",
            search: filters.search ?? "",
          }}
          onApply={applyFilters}
          onReset={resetFilters}
        >
          {(draft, setDraft) => (
            <>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Sumber</span>
                <Select
                  value={draft.source}
                  onChange={(e) => setDraft({ ...draft, source: e.target.value })}
                >
                  <option value="">Semua Sumber</option>
                  {filter_options.sources.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Status</span>
                <Select
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                >
                  <option value="">Semua Status</option>
                  {filter_options.statuses.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">
                  Cari sumber / filename
                </span>
                <Input
                  type="search"
                  value={draft.search}
                  onChange={(e) => setDraft({ ...draft, search: e.target.value })}
                />
              </label>
            </>
          )}
        </MobileFilterSheet>

        {/* Mobile sort sheet */}
        <MobileSortSheet
          open={sortOpen}
          onOpenChange={setSortOpen}
          current={{ sort, direction }}
          options={SORT_OPTIONS}
          onSelect={(opt) => {
            navigate({ sort: opt.sort, direction: opt.direction })
            setSortOpen(false)
          }}
        />
      </AppShell>
    </>
  )
}

// ---------------------------------------------------------------------------
// Source-folder combobox (feature-local; existing folders + free-text new)
// ---------------------------------------------------------------------------

function SourceFolderCombobox({
  id,
  value,
  onChange,
  options,
  disabled,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  options: string[]
  disabled?: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [open])

  const filtered = value
    ? options.filter((o) => o.toLowerCase().includes(value.toLowerCase()))
    : options

  return (
    <div ref={containerRef} className="relative mt-1">
      <div className="relative">
        <Input
          id={id}
          value={value}
          placeholder="pilih folder existing atau ketik nama baru…"
          autoComplete="off"
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          className="pr-8"
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className="absolute inset-y-0 right-2 flex items-center text-ink-muted"
          aria-label="Tampilkan daftar folder"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>
      {open && filtered.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-hairline bg-surface py-1 shadow-lg">
          {filtered.map((o) => (
            <li key={o}>
              <button
                type="button"
                className="w-full px-3 py-1.5 text-left text-sm text-ink-body hover:bg-accent-faded"
                onClick={() => {
                  onChange(o)
                  setOpen(false)
                }}
              >
                {o}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SortableHeader({
  col,
  label,
  sort,
  direction,
  onSort,
  align = "left",
}: {
  col: string
  label: string
  sort: string
  direction: "asc" | "desc"
  onSort: (col: string) => void
  align?: "left" | "right"
}) {
  const active = sort === col
  return (
    <th
      className={`cursor-pointer select-none px-4 py-2.5 font-medium text-ink-muted hover:text-ink-body text-${align}`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          direction === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </span>
    </th>
  )
}

function RunTableRow({
  run,
  selected,
  onToggleSelect,
  onCancel,
  onDelete,
  onRegenerate,
}: {
  run: RunRow
  selected: boolean
  onToggleSelect: () => void
  onCancel: () => void
  onDelete: () => void
  onRegenerate: () => void
}) {
  const isInFlight = run.status === "pending" || run.status === "processing"

  return (
    <tr>
      <td className="px-3 py-3">
        <Checkbox
          checked={selected}
          onChange={onToggleSelect}
          disabled={isInFlight}
          aria-label={`Pilih run ${run.source_folder}`}
        />
      </td>
      <td className="px-4 py-3 font-medium text-ink-display">
        <Link href={`/admin/data-xray/${run.id}`} className="hover:text-accent hover:underline">
          {run.source_folder}
        </Link>
      </td>
      <td className="max-w-[200px] truncate px-4 py-3 text-xs text-ink-muted" title={run.subpath ?? ""}>
        {run.subpath ?? "—"}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-body">{run.file_count}</td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-body">
        {formatBytes(run.total_bytes)}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={run.status} />
        <ResultBadges run={run} />
        {run.error_message && (
          <p className="mt-1 max-w-[240px] truncate text-xs text-red-600 dark:text-red-400" title={run.error_message}>
            {run.error_message}
          </p>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-ink-muted">{run.uploaded_by ?? "—"}</td>
      <td className="px-4 py-3 text-xs text-ink-muted">{formatDate(run.created_at)}</td>
      <td className="px-2 py-3">
        {isInFlight ? (
          <div className="flex items-center justify-end gap-1">
            <Link
              href={`/admin/data-xray/${run.id}`}
              title="Lihat detail"
              className="rounded p-1 text-ink-muted transition-colors hover:text-accent"
            >
              <Eye className="h-3.5 w-3.5" />
            </Link>
            <button
              onClick={onCancel}
              title="Batalkan run ini"
              className="rounded p-1 text-ink-muted transition-colors hover:text-red-600"
            >
              <Ban className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1">
            <Link
              href={`/admin/data-xray/${run.id}`}
              title="Lihat detail"
              className="rounded p-1 text-ink-muted transition-colors hover:text-accent"
            >
              <Eye className="h-3.5 w-3.5" />
            </Link>
            <button
              onClick={onRegenerate}
              title="Regenerate X-Ray"
              className="rounded p-1 text-ink-muted transition-colors hover:text-accent"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onDelete}
              title="Hapus run ini"
              className="rounded p-1 text-ink-muted transition-colors hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

function RunCard({
  run,
  selected,
  onToggleSelect,
  onCancel,
  onDelete,
  onRegenerate,
}: {
  run: RunRow
  selected: boolean
  onToggleSelect: () => void
  onCancel: () => void
  onDelete: () => void
  onRegenerate: () => void
}) {
  const isInFlight = run.status === "pending" || run.status === "processing"

  return (
    <DataCard>
      <DataCardHeader>
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <Checkbox
            checked={selected}
            onChange={onToggleSelect}
            disabled={isInFlight}
            aria-label={`Pilih run ${run.source_folder}`}
            className="mt-0.5 shrink-0"
          />
          <DataCardTitle>
            <Link href={`/admin/data-xray/${run.id}`} className="break-all hover:text-accent hover:underline">
              {run.source_folder}
            </Link>
          </DataCardTitle>
        </div>
        <DataCardStatus><StatusBadge status={run.status} /></DataCardStatus>
      </DataCardHeader>
      <DataCardGrid>
        <DataCardField label="Subpath" value={run.subpath ?? "—"} />
        <DataCardField label="File" value={String(run.file_count)} />
        <DataCardField label="Ukuran" value={formatBytes(run.total_bytes)} />
        <DataCardField label="Oleh" value={run.uploaded_by ?? "—"} />
        <DataCardField wide label="Waktu" value={formatDate(run.created_at)} />
        {run.error_message && (
          <DataCardField wide label="Error" value={run.error_message} />
        )}
      </DataCardGrid>
      <ResultBadges run={run} />
      {run.status === "completed" && run.source_mapped === false && (
        <p className="mt-2 text-xs text-ink-muted">
          Sumber baru — x-ray struktural saja. Onboard ke <code>CTX</code> agar konteks bisnis terisi.
        </p>
      )}
      <DataCardActions>
        <Button asChild variant="ghost" size="sm" className="gap-2">
          <Link href={`/admin/data-xray/${run.id}`}>
            <Eye className="h-4 w-4" />
            Detail
          </Link>
        </Button>
        {isInFlight ? (
          <Button variant="ghost" size="sm" onClick={onCancel} className="gap-2 text-red-600">
            <Ban className="h-4 w-4" />
            Batalkan
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={onRegenerate} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Regenerate
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} className="gap-2 text-red-600">
              <Trash2 className="h-4 w-4" />
              Hapus
            </Button>
          </>
        )}
      </DataCardActions>
    </DataCard>
  )
}

// Result digest shown on a completed run: meta-file count, 🆕 new templates,
// 🔴 integrity issues, ingest_ok summary, and a mapped/`(belum dipetakan)` chip.
function ResultBadges({ run }: { run: RunRow }) {
  if (run.status !== "completed") return null

  const hasMeta = run.meta_files_written != null
  const hasIngest = run.ingest_ok_count != null
  const hasMapped = run.source_mapped != null
  if (!hasMeta && !hasIngest && !hasMapped && run.new_template_count === 0 && run.integrity_issue_count === 0) {
    return null
  }

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {hasMeta && (
        <Badge tone="muted">
          <FileSpreadsheet className="mr-1 h-3 w-3" />
          {run.meta_files_written} meta
        </Badge>
      )}
      {run.new_template_count > 0 && (
        <Badge tone="accent">
          <FilePlus2 className="mr-1 h-3 w-3" />
          {run.new_template_count} template baru
        </Badge>
      )}
      {run.integrity_issue_count > 0 && (
        <Badge tone="danger">
          <ShieldAlert className="mr-1 h-3 w-3" />
          {run.integrity_issue_count} korup
        </Badge>
      )}
      {hasIngest && (
        <Badge tone="success">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          {run.ingest_ok_count}/{run.file_count} ingest_ok
        </Badge>
      )}
      {hasMapped &&
        (run.source_mapped ? (
          <Badge tone="success">termapping</Badge>
        ) : (
          <Badge tone="muted">belum dipetakan</Badge>
        ))}
    </div>
  )
}

function StatusBadge({ status }: { status: RunStatus }) {
  switch (status) {
    case "pending":
      return (
        <Badge tone="muted">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      )
    case "processing":
      return (
        <Badge tone="accent">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Processing
        </Badge>
      )
    case "completed":
      return (
        <Badge tone="success">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Completed
        </Badge>
      )
    case "failed":
      return (
        <Badge tone="danger">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      )
    case "cancelled":
      return (
        <Badge tone="muted">
          <Ban className="mr-1 h-3 w-3" />
          Dibatalkan
        </Badge>
      )
  }
}

function ProgressBar({ value }: { value?: number }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-hairline">
      <div
        className="h-full rounded-full bg-accent transition-all duration-300"
        style={{ width: `${Math.min(value ?? 0, 100)}%` }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function getCsrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ""
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
